import _ from 'lodash';
import {
  AI_CSAT_COOLDOWN_MS,
  AI_CSAT_HANDLED_STORAGE_KEY,
  AI_CSAT_LAST_ASKED_STORAGE_KEY,
  AI_CSAT_PENDING_MAX_AGE_MS,
  AI_CSAT_PENDING_STORAGE_KEY,
  CSAT_RATED_MAX_ENTRIES,
} from '../constants/chat';
import { readIdList, rememberId } from './storedIdList';

// Decides when the AI chat CSAT popup (ChatCsatPopup) may be shown.
//
// Chatbot writes a single "pending" candidate after every assistant answer, so a
// conversation the customer leaves by killing the app still has a record on the
// next open. AiChatCsatHost reads it when the chat closes and when the app frame
// mounts. Any escalation to the team withdraws the candidate; the team CSAT
// covers that thread instead.
//
// Showing the popup consumes the candidate (so an app kill with the popup open
// cannot re-ask); the 14-day cooldown and the handled list are written only when
// the customer answers or dismisses, so a failed submit costs nothing.

const defaultStorage = () => (typeof localStorage === 'undefined' ? null : localStorage);

function readJson(storage, key, fallback) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
}

function writeJson(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Full or blocked store: the customer is asked one time fewer, nothing else.
  }
}

function removeKey(storage, key) {
  try {
    storage.removeItem(key);
  } catch (error) {
    // Blocked store: the stale key is simply left behind.
  }
}

function readPending(storage) {
  const pending = readJson(storage, AI_CSAT_PENDING_STORAGE_KEY, null);
  if (!pending || !pending.conversationId || !Number.isFinite(pending.at)) {
    return null;
  }
  return pending;
}

function isHandled(conversationId, storage) {
  return readIdList(AI_CSAT_HANDLED_STORAGE_KEY, storage).includes(String(conversationId));
}

// Only the matching candidate: a missing id must not wipe another conversation's.
export function clearCandidate(conversationId, storage = defaultStorage()) {
  if (!storage || !conversationId) {
    return;
  }
  const pending = readPending(storage);
  if (pending && String(pending.conversationId) === String(conversationId)) {
    removeKey(storage, AI_CSAT_PENDING_STORAGE_KEY);
  }
}

// Escalated threads in a fetched conversations list: the chat entries that carry
// an escalation, plus the parents of the intercom entries.
export function escalatedConversationIds(conversations) {
  return _.chain(conversations)
    .map(c => {
      if (c.type === 'intercom') { return c.cashflowAssistantConversationId; }
      return c.escalation ? c.conversation_id : null;
    })
    .compact()
    .uniq()
    .value();
}

// Withdraws the candidate when its conversation turns out to be escalated.
// Chatbot clears the candidate when it escalates a thread itself; this covers
// the thread it only finds out about later (escalated on another device, or a
// send whose response never came back), where the candidate would otherwise
// outlive the hand-over and ask for a rating the team CSAT already covers.
export function clearCandidateIn(conversationIds, storage = defaultStorage()) {
  if (!storage || !conversationIds || !conversationIds.length) {
    return;
  }
  const pending = readPending(storage);
  if (pending && conversationIds.some(id => String(id) === String(pending.conversationId))) {
    removeKey(storage, AI_CSAT_PENDING_STORAGE_KEY);
  }
}

export function recordCandidate(
  { conversationId, escalated = false },
  { storage = defaultStorage(), now = Date.now() } = {},
) {
  if (!storage || !conversationId) {
    return;
  }
  if (escalated) {
    clearCandidate(conversationId, storage);
    return;
  }
  if (isHandled(conversationId, storage)) {
    return;
  }
  writeJson(storage, AI_CSAT_PENDING_STORAGE_KEY, { conversationId: String(conversationId), at: now });
}

// Expired and already-handled candidates are dropped on the way; a candidate
// inside the cooldown is left in place and simply ages out, because the cooldown
// is longer than the max age.
export function eligibleCandidate({ storage = defaultStorage(), now = Date.now() } = {}) {
  if (!storage) {
    return null;
  }
  const pending = readPending(storage);
  if (!pending) {
    return null;
  }
  if (now - pending.at > AI_CSAT_PENDING_MAX_AGE_MS || isHandled(pending.conversationId, storage)) {
    removeKey(storage, AI_CSAT_PENDING_STORAGE_KEY);
    return null;
  }
  const lastAsked = Number(readJson(storage, AI_CSAT_LAST_ASKED_STORAGE_KEY, 0));
  if (Number.isFinite(lastAsked) && lastAsked > 0 && now - lastAsked < AI_CSAT_COOLDOWN_MS) {
    return null;
  }
  return { conversationId: String(pending.conversationId) };
}

// The customer answered or dismissed: start the cooldown, never ask about this
// conversation again.
export function markHandled(conversationId, { storage = defaultStorage(), now = Date.now() } = {}) {
  if (!storage) {
    return;
  }
  writeJson(storage, AI_CSAT_LAST_ASKED_STORAGE_KEY, now);
  rememberId(AI_CSAT_HANDLED_STORAGE_KEY, conversationId, CSAT_RATED_MAX_ENTRIES, storage);
  clearCandidate(conversationId, storage);
}
